import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { prefetchRoute } from '../../routes/prefetch';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Box,
  Divider,
  Collapse,
} from '@mui/material';
import {
  Dashboard,
  People,
  Inventory,
  Receipt,
  Payment,
  Support,
  Router,
  Assessment,
  Settings,
  Wifi,
  Storefront,
  Lan,
  WifiTethering,
  Group,
  ExpandLess,
  ExpandMore,
  MonitorHeart,
} from '@mui/icons-material';

const drawerWidth = 280;

const menuItems = [
  {
    text: 'Dashboard',
    icon: <Dashboard />,
    path: '/',
    description: 'Overview',
  },
  {
    text: 'Customers',
    icon: <People />,
    path: '/customers',
    description: 'Manage Customers',
    children: [
    ],
  },
  {
    text: 'Packages & Parents',
    icon: <Inventory />,
    path: '/packages-and-parents',
    description: 'Packages & Parent Queues',
  },
  {
    text: 'Billing',
    icon: <Receipt />,
    path: '/billing',
    description: 'Invoices & Billing',
  },
  {
    text: 'Payments',
    icon: <Payment />,
    path: '/payments',
    description: 'Payment Management',
  },
  {
    text: 'Support',
    icon: <Support />,
    path: '/support',
    description: 'Customer Support',
  },
  {
    text: 'Network',
    icon: <Router />,
    path: '/network',
    description: 'Network Management',
    noSubmenu: true,
    children: [
      {
        text: 'Hotspot Server',
        icon: <WifiTethering />,
        path: '/network/hotspot-server',
        description: 'Configure hotspot',
      },
      {
        text: 'Hotspot Users',
        icon: <Group />,
        path: '/network/hotspot-users',
        description: 'Manage hotspot users',
      },
      {
        text: 'VLAN Management',
        icon: <Lan />,
        path: '/network/vlans',
        description: 'Manage VLANs',
      },
      {
        text: 'Monitoring',
        icon: <MonitorHeart />,
        path: '/network/monitoring',
        description: 'Router health & alerts',
      },
    ],
  },
  {
    text: 'Reports',
    icon: <Assessment />,
    path: '/reports',
    description: 'Analytics & Reports',
  },
  {
    text: 'Settings',
    icon: <Settings />,
    path: '/settings',
    description: 'System Settings',
  },
  {
    text: 'Shop',
    icon: <Storefront />,
    path: '/shop',
    description: 'Order devices & add-ons',
  },
];

const Sidebar = ({ open, onToggle, isMobile }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [expandedMenus, setExpandedMenus] = useState({});

  // Auto-expand parent menu when navigating to a child route
  useEffect(() => {
    menuItems.forEach((item) => {
      if (item.children && item.children.length > 0 && !item.noSubmenu) {
        const hasActiveChild = item.children.some((child) =>
          location.pathname.startsWith(child.path)
        );
        if (hasActiveChild) {
          setExpandedMenus((prev) => ({ ...prev, [item.text]: true }));
        }
      }
    });
  }, [location.pathname]);

  const toggleSubmenu = (menuText) => {
    setExpandedMenus((prev) => ({
      ...prev,
      [menuText]: !prev[menuText],
    }));
  };

  const handleItemClick = (item) => {
    const hasSubmenu = item.children && item.children.length > 0 && !item.noSubmenu;
    if (hasSubmenu) {
      toggleSubmenu(item.text);
      // Auto-navigate to parent path only when expanding
      if (!expandedMenus[item.text]) {
        navigate(item.path);
      }
    } else {
      navigate(item.path);
    }
    if (isMobile) {
      onToggle();
    }
  };

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/' || location.pathname === '/dashboard';
    }
    return location.pathname.startsWith(path);
  };

  const drawerContent = (
    <>
      <Toolbar sx={{ px: 3, bgcolor: 'primary.main', color: 'white' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
          <Wifi sx={{ mr: 2, fontSize: 32 }} />
          <Box>
            <Typography variant="h6" noWrap component="div">
              BUZZKILL ISP
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
              Management System
            </Typography>
          </Box>
        </Box>
      </Toolbar>

      <Divider />

      <List sx={{ px: 1, py: 2 }}>
        {menuItems.map((item) => (
          <React.Fragment key={item.text}>
            <ListItem disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                onClick={() => handleItemClick(item)}
                // Warm the section's chunk before the click lands, so the route
                // swaps content instead of flashing a loader (see routes/prefetch.js).
                onMouseEnter={() => prefetchRoute(item.path)}
                onFocus={() => prefetchRoute(item.path)}
                onTouchStart={() => prefetchRoute(item.path)}
                selected={(!item.children?.length || item.noSubmenu) && isActive(item.path)}
                sx={{
                  borderRadius: 2,
                  mx: 1,
                  '&.Mui-selected': {
                    backgroundColor: 'primary.main',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'primary.dark',
                    },
                    '& .MuiListItemIcon-root': {
                      color: 'white',
                    },
                  },
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    color: isActive(item.path) ? 'white' : 'action.active',
                    minWidth: 40,
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  secondary={item.description}
                  primaryTypographyProps={{
                    fontWeight: isActive(item.path) ? 600 : 400,
                    fontSize: '0.9rem',
                  }}
                  secondaryTypographyProps={{
                    fontSize: '0.75rem',
                    color: isActive(item.path) ? 'rgba(255,255,255,0.7)' : 'text.secondary',
                  }}
                />
                {item.children && item.children.length > 0 && !item.noSubmenu && (
                  expandedMenus[item.text] ? <ExpandLess /> : <ExpandMore />
                )}
              </ListItemButton>
            </ListItem>
            {/* Render collapsible children if present */}
            {item.children && item.children.length > 0 && !item.noSubmenu && (
              <Collapse in={!!expandedMenus[item.text]} timeout="auto" unmountOnExit>
                <List component="div" disablePadding sx={{ pl: 4 }}>
                  {item.children.map((child) => (
                    <ListItem key={child.text} disablePadding sx={{ mb: 0.5 }}>
                      <ListItemButton
                        onClick={() => handleItemClick(child)}
                        onMouseEnter={() => prefetchRoute(child.path)}
                        onFocus={() => prefetchRoute(child.path)}
                        onTouchStart={() => prefetchRoute(child.path)}
                        selected={isActive(child.path)}
                        sx={{
                          borderRadius: 2,
                          mx: 1,
                          '&.Mui-selected': {
                            backgroundColor: 'primary.main',
                            color: 'white',
                            '&:hover': {
                              backgroundColor: 'primary.dark',
                            },
                            '& .MuiListItemIcon-root': {
                              color: 'white',
                            },
                          },
                          '&:hover': {
                            backgroundColor: 'action.hover',
                          },
                        }}
                      >
                        {child.icon && (
                          <ListItemIcon sx={{ color: isActive(child.path) ? 'white' : 'action.active', minWidth: 40 }}>
                            {child.icon}
                          </ListItemIcon>
                        )}
                        <ListItemText
                          primary={child.text}
                          secondary={child.description}
                          primaryTypographyProps={{
                            fontWeight: isActive(child.path) ? 600 : 400,
                            fontSize: '0.9rem',
                          }}
                          secondaryTypographyProps={{
                            fontSize: '0.75rem',
                            color: isActive(child.path) ? 'rgba(255,255,255,0.7)' : 'text.secondary',
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            )}
          </React.Fragment>
        ))}
      </List>

      <Box sx={{ flexGrow: 1 }} />

      <Box sx={{ p: 2, mt: 'auto' }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          Version 1.0.0
        </Typography>
        <Typography variant="caption" color="text.secondary">
          © 2024 BUZZKILL ISP
        </Typography>
      </Box>
    </>
  );

  if (isMobile) {
    return (
      <Drawer
        variant="temporary"
        open={open}
        onClose={onToggle}
        id="app-navigation-drawer"
        ModalProps={{
          keepMounted: true, // Better mobile performance
        }}
        sx={{
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: drawerWidth,
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        {drawerContent}
      </Drawer>
    );
  }

  return (
    <Drawer
      variant="persistent"
      open={open}
      id="app-navigation-drawer"
      sx={{
        width: open ? drawerWidth : 0,
        flexShrink: 0,
        // A collapsed persistent drawer still mounts its 280px paper, so the root
        // has to clip it: otherwise the off-canvas panel stays clickable and
        // widens the page (the horizontal-scroll bug in Chrome/Edge).
        overflow: 'hidden',
        transition: (theme) =>
          theme.transitions.create('width', { duration: theme.transitions.duration.shorter }),
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          transform: open ? 'none' : `translateX(-${drawerWidth}px)`,
          transition: (theme) =>
            theme.transitions.create('transform', { duration: theme.transitions.duration.shorter }),
          borderRight: '1px solid',
          borderColor: 'divider',
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
};

export default Sidebar;
